import React, { useMemo, useState } from "react";
import {
  FiX,
  FiEdit3,
  FiSend,
  FiCheck,
  FiCornerUpLeft,
  FiPlus,
  FiAlertCircle,
} from "react-icons/fi";

/**
 * Write and approve release notes from inside the product.
 *
 * This exists so publishing a release note doesn't require database
 * credentials. Database access is all-or-nothing — any table, any row, no
 * record of who changed what — which is both far more than the job needs and
 * an awkward thing to ask of the people who should be writing these.
 *
 * Two permissions, deliberately separate:
 *   - authors  draft, edit and submit
 *   - approvers publish or send back with a reason
 *
 * An author cannot approve their own note. Without that the approval step is
 * decorative, and the server enforces it regardless of what this renders —
 * `canApprove` arrives precomputed per row so the rule isn't reimplemented
 * here and subtly disagreed with.
 */

// Matches NVARCHAR(1000) on mn.ReleaseNotes.Title and the API's own cap.
const MAX_TITLE_LENGTH = 1000;

const BLANK = {
  code: "",
  title: "",
  date: "",
  highlights: "",
  tags: "",
  guidance: "",
};

const STATUS_CLS = {
  Draft: "is-draft",
  "In review": "is-review",
};

// Highlights and tags are edited as lines/commas and stored as arrays — the
// form shouldn't make anyone think about JSON.
const toLines = (s) =>
  (s || "")
    .split("\n")
    .map((x) => x.trim())
    .filter(Boolean);
const toCsv = (s) =>
  (s || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

const ReleaseEditorModal = ({
  open,
  onClose,
  drafts = [],
  canAuthor = false,
  canApprove = false,
  currentUser,
  focusCode = "",
  onSave,
  onSubmit,
  onReview,
}) => {
  const [form, setForm] = useState(BLANK);
  const [editingCode, setEditingCode] = useState(null); // null = not editing
  const [busy, setBusy] = useState(false);
  const [busyCode, setBusyCode] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [sendBackFor, setSendBackFor] = useState(null);
  const [sendBackNote, setSendBackNote] = useState("");

  const awaiting = useMemo(
    () => drafts.filter((d) => d.status === "In review"),
    [drafts],
  );
  const inProgress = useMemo(
    () => drafts.filter((d) => d.status === "Draft"),
    [drafts],
  );

  // An emailed link can outlive the thing it points at — someone else may have
  // published or sent the note back in the meantime. Saying so beats silently
  // opening a queue with no sign of the note the approver came here for.
  const linkedNoteMissing =
    Boolean(focusCode) && !drafts.some((d) => d.id === focusCode);

  if (!open) return null;

  const close = () => {
    setForm(BLANK);
    setEditingCode(null);
    setError("");
    setNotice("");
    setSendBackFor(null);
    onClose();
  };

  const startNew = () => {
    setForm(BLANK);
    setEditingCode("");
    setError("");
    setNotice("");
  };

  const startEdit = (d) => {
    setForm({
      code: d.id,
      title: d.title,
      date: d.date || "",
      highlights: (d.highlights || []).join("\n"),
      tags: (d.tags || []).join(", "),
      guidance: d.guidance || "",
    });
    setEditingCode(d.id);
    setError("");
    setNotice("");
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.title.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      await onSave({
        code: form.code.trim(),
        title: form.title.trim(),
        date: form.date,
        highlights: toLines(form.highlights),
        tags: toCsv(form.tags),
        guidance: form.guidance.trim(),
        author: currentUser?.name || "",
      });
      setNotice(`Saved ${form.code.trim()} as a draft.`);
      setEditingCode(null);
      setForm(BLANK);
    } catch (err) {
      setError(err?.message || "Couldn't save the draft.");
    } finally {
      setBusy(false);
    }
  };

  const submit = async (d) => {
    setBusyCode(d.id);
    setError("");
    try {
      await onSubmit(d.id);
      setNotice(`${d.id} sent for approval.`);
    } catch (err) {
      setError(err?.message || "Couldn't submit for approval.");
    } finally {
      setBusyCode(null);
    }
  };

  const decide = async (d, approve) => {
    setBusyCode(d.id);
    setError("");
    try {
      await onReview({
        code: d.id,
        approve,
        note: approve ? "" : sendBackNote.trim(),
        approver: currentUser?.name || "",
      });
      setNotice(approve ? `${d.id} published.` : `${d.id} sent back.`);
      setSendBackFor(null);
      setSendBackNote("");
    } catch (err) {
      setError(err?.message || "Couldn't record the decision.");
    } finally {
      setBusyCode(null);
    }
  };

  const renderCard = (d) => (
    <div
      key={d.id}
      className={`rn-draft ${d.id === focusCode ? "is-linked" : ""}`}
    >
      <div className="rn-draft-head">
        <span className="rn-draft-code">{d.id}</span>
        <span className={`rn-draft-status ${STATUS_CLS[d.status] || ""}`}>
          {d.status}
        </span>
      </div>
      <div className="rn-draft-title">{d.title}</div>
      <div className="rn-draft-meta">
        {d.authoredBy ? <>Written by {d.authoredBy}</> : "Unattributed"}
        {d.date ? <> · {d.date}</> : null}
        {d.highlights?.length ? (
          <>
            {" "}
            · {d.highlights.length} highlight
            {d.highlights.length === 1 ? "" : "s"}
          </>
        ) : null}
      </div>

      {/* A sent-back note is criticism the author has to act on, so it stays
          visible on the card rather than living in an email. */}
      {d.reviewNote ? (
        <div className="rn-draft-sentback">
          <FiAlertCircle size={13} />
          <span>Sent back: {d.reviewNote}</span>
        </div>
      ) : null}

      <div className="rn-draft-actions">
        {canAuthor && d.status === "Draft" && (
          <>
            <button
              type="button"
              className="rn-admin-cancel"
              onClick={() => startEdit(d)}
            >
              Edit
            </button>
            <button
              type="button"
              className="rn-admin-save"
              onClick={() => submit(d)}
              disabled={busyCode === d.id}
            >
              <FiSend size={12} />{" "}
              {busyCode === d.id ? "Sending…" : "Submit for approval"}
            </button>
          </>
        )}

        {d.status === "In review" && d.canApprove && (
          <>
            <button
              type="button"
              className="rn-admin-save"
              onClick={() => decide(d, true)}
              disabled={busyCode === d.id}
            >
              <FiCheck size={12} /> {busyCode === d.id ? "…" : "Publish"}
            </button>
            <button
              type="button"
              className="rn-admin-cancel"
              onClick={() => {
                setSendBackFor(sendBackFor === d.id ? null : d.id);
                setSendBackNote("");
              }}
              disabled={busyCode === d.id}
            >
              <FiCornerUpLeft size={12} /> Send back
            </button>
          </>
        )}

        {/* Explains the absence of buttons rather than leaving a dead card. */}
        {d.status === "In review" && !d.canApprove && (
          <span className="rn-draft-waiting">
            {d.isMine
              ? "You wrote this — it needs a different approver."
              : "Awaiting approval."}
          </span>
        )}
      </div>

      {sendBackFor === d.id && (
        <div className="rn-sendback">
          <textarea
            className="rn-request-textarea"
            rows={2}
            placeholder="What needs changing before this can be published?"
            value={sendBackNote}
            onChange={(e) => setSendBackNote(e.target.value)}
          />
          <div className="rn-admin-actions">
            <button
              type="button"
              className="rn-admin-save"
              onClick={() => decide(d, false)}
              disabled={busyCode === d.id || !sendBackNote.trim()}
            >
              Send back
            </button>
            <button
              type="button"
              className="rn-admin-cancel"
              onClick={() => setSendBackFor(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="rn-modal-overlay" onClick={close}>
      <div
        className="rn-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Release notes"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="rn-modal-head">
          <div className="rn-modal-title">
            <FiEdit3 size={18} />
            <div>
              <h2>Release Notes</h2>
              <p>Write a note, send it for approval, publish it.</p>
            </div>
          </div>
          <button
            type="button"
            className="rn-modal-close"
            onClick={close}
            aria-label="Close"
          >
            <FiX size={18} />
          </button>
        </div>

        <div className="rn-modal-body">
          <div className="rn-owner-note">
            <span>
              Notes are written by the engineering team and published only
              after approval. Nothing here is visible to anyone else until it
              is published.
            </span>
          </div>

          {linkedNoteMissing ? (
            <div className="rn-draft-sentback">
              <FiAlertCircle size={13} />
              <span>
                <strong>{focusCode}</strong> is no longer awaiting approval —
                it may already have been published or sent back.
              </span>
            </div>
          ) : null}

          {notice ? <div className="rn-draft-notice">{notice}</div> : null}
          {error ? <div className="rn-request-error">{error}</div> : null}

          {/* ── Editor ── */}
          {canAuthor && editingCode === null && (
            <button type="button" className="rn-request-btn" onClick={startNew}>
              <FiPlus size={15} /> New release note
            </button>
          )}

          {canAuthor && editingCode !== null && (
            <form className="rn-release-form" onSubmit={save}>
              {/* Each caption is wrapped in a single element on purpose: the
                  label is a column flexbox, so a bare text node and the
                  "(optional)" hint would otherwise become two separate items
                  and stack on top of each other. */}
              <div className="rn-form-row">
                <label className="rn-request-label">
                  <span className="rn-field-label">Version</span>
                  <input
                    className="rn-request-input"
                    type="text"
                    placeholder="v1.7.0"
                    maxLength={50}
                    value={form.code}
                    disabled={Boolean(editingCode)}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, code: e.target.value }))
                    }
                  />
                </label>
                <label className="rn-request-label">
                  <span className="rn-field-label">Release date</span>
                  <input
                    className="rn-request-input"
                    type="date"
                    value={form.date}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, date: e.target.value }))
                    }
                  />
                </label>
              </div>

              <label className="rn-request-label">
                <span className="rn-field-label">Title</span>
                <input
                  className="rn-request-input"
                  type="text"
                  placeholder="What this release is about"
                  maxLength={MAX_TITLE_LENGTH}
                  value={form.title}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, title: e.target.value }))
                  }
                />
                <div
                  className={`rn-char-count ${
                    form.title.length >= MAX_TITLE_LENGTH ? "is-max" : ""
                  }`}
                  aria-live="polite"
                >
                  {form.title.length}/{MAX_TITLE_LENGTH}
                </div>
              </label>

              <label className="rn-request-label">
                <span className="rn-field-label">
                  What's new <span className="rn-optional">(one per line)</span>
                </span>
                <textarea
                  className="rn-request-textarea"
                  rows={4}
                  placeholder={"Navigate between calls without leaving the page\nCall Review Sheet is editable for every call type"}
                  value={form.highlights}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, highlights: e.target.value }))
                  }
                />
              </label>

              <label className="rn-request-label">
                <span className="rn-field-label">
                  How to use it <span className="rn-optional">(guidance)</span>
                </span>
                <textarea
                  className="rn-request-textarea"
                  rows={3}
                  placeholder="Plain guidance for someone who has to use this tomorrow"
                  value={form.guidance}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, guidance: e.target.value }))
                  }
                />
              </label>

              <label className="rn-request-label">
                <span className="rn-field-label">
                  Tags <span className="rn-optional">(comma separated)</span>
                </span>
                <input
                  className="rn-request-input"
                  type="text"
                  placeholder="Insights, Reporting"
                  value={form.tags}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, tags: e.target.value }))
                  }
                />
              </label>

              <div className="rn-request-actions is-standalone">
                <button
                  type="button"
                  className="rn-admin-cancel"
                  onClick={() => {
                    setEditingCode(null);
                    setForm(BLANK);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rn-request-submit"
                  disabled={!form.code.trim() || !form.title.trim() || busy}
                >
                  {busy ? "Saving…" : "Save draft"}
                </button>
              </div>
            </form>
          )}

          {/* ── Awaiting approval ── */}
          {awaiting.length > 0 && (
            <>
              <div className="rn-draft-section">
                {awaiting.length} awaiting approval
              </div>
              {awaiting.map(renderCard)}
            </>
          )}

          {/* ── Drafts ── */}
          {inProgress.length > 0 && (
            <>
              <div className="rn-draft-section">
                {inProgress.length} draft{inProgress.length === 1 ? "" : "s"}
              </div>
              {inProgress.map(renderCard)}
            </>
          )}

          {drafts.length === 0 && editingCode === null && (
            <div className="rn-empty">
              Nothing in progress. Everything written so far is published.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReleaseEditorModal;
